<script setup lang="ts">
import {
  fromMaybe,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { trimmedMaybe } from "../lib/trimmedString";
import { computed } from "vue";
import { assertNever } from "../lib/assertNever";
import {
  entityChipAccentStyle,
  entityEffectiveColor,
  entityGraphNodeLabelStyle,
} from "../lib/entityColors";
import { displayFileName } from "../lib/fileNames";
import {
  CUSTOM_RELATION_OPTION,
  buildCustomRelationPair,
  mergedRelationPairs,
  relationLabel,
  relationSidesFromPairs,
} from "../lib/entityLinks";
import { useTea } from "../lib/tea";
import { newDatabaseRelationPairId } from "../lib/zwConfig";
import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectSettingsStore } from "../stores/useProjectSettingsStore";
import {
  initialCustomRelationDialogModel,
  updateCustomRelationDialogFn,
  type CustomRelationDialogCmd,
} from "./EntityRelationshipDialog.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwMenuItem from "./ui/ZwMenuItem.vue";
import ZwSearchableMenu from "./ui/ZwSearchableMenu.vue";

const props = withDefaults(
  defineProps<{
    open: boolean;
    sourceFile: string;
    editing: boolean;
    busy?: boolean;
    linkableEntries: { value: string; label: string }[];
    /** When false, the target chip is display-only (create-from-drag already set it). */
    allowTargetChange?: boolean;
    targetFile: string;
    relation: string;
    note: string;
  }>(),
  {
    busy: false,
    allowTargetChange: true,
  },
);

const emit = defineEmits<{
  close: [];
  save: [];
  remove: [];
  "update:targetFile": [value: string];
  "update:relation": [value: string];
  "update:note": [value: string];
}>();

const session = useEditorSessionStore();
const outlineEntities = useOutlineEntitiesStore();
const entityFields = useActiveEntityFieldsStore();
const projectSettings = useProjectSettingsStore();

async function runCustomRelationCmd(
  cmd: CustomRelationDialogCmd,
): Promise<void> {
  switch (cmd.kind) {
    case "addPair": {
      const pairM = buildCustomRelationPair(
        cmd.draft,
        trimmedMaybe(cmd.inverseDraft),
        newDatabaseRelationPairId,
      );
      if (isNothing(pairM)) return;
      await projectSettings.addDatabaseRelationPair(pairM.value);
      emit("update:relation", pairM.value.aId);
      void dispatchCustomRelation({ type: "CommitSucceeded" });
      return;
    }
    default:
      assertNever(cmd as never);
  }
}

const { model: customRelationModel, dispatch: dispatchCustomRelation } = useTea(
  initialCustomRelationDialogModel,
  updateCustomRelationDialogFn,
  { runCmd: just(runCustomRelationCmd) },
);

const customRelationDialogOpen = computed(() => customRelationModel.value.open);
const customRelationDraft = computed({
  get: (): string => customRelationModel.value.draft,
  set: (draft: string): void => {
    void dispatchCustomRelation({ type: "DraftChanged", draft });
  },
});
const customRelationInverseDraft = computed({
  get: (): string => customRelationModel.value.inverseDraft,
  set: (inverseDraft: string): void => {
    void dispatchCustomRelation({ type: "InverseChanged", inverseDraft });
  },
});

const title = computed(() =>
  props.editing ? "Edit relationship" : "Add relationship",
);

const targetLabel = computed(() =>
  props.targetFile.length > 0
    ? displayFileName(props.targetFile)
    : "Choose entry…",
);

const customPairs = computed(
  (): Maybe<readonly import("../lib/zwConfig").ZwDatabaseRelationPair[]> =>
    matchMaybe(session.projectConfig, {
      just: (
        config,
      ): Maybe<readonly import("../lib/zwConfig").ZwDatabaseRelationPair[]> =>
        just(config.databaseRelationPairs),
      nothing: (): Maybe<
        readonly import("../lib/zwConfig").ZwDatabaseRelationPair[]
      > => nothing,
    }),
);

const relationOptions = computed(() => {
  const sides = relationSidesFromPairs(mergedRelationPairs(customPairs.value));
  const current = props.relation;
  const known = sides.some((side) => side.id === current);
  return [
    ...sides.map((side) => ({ value: side.id, label: side.label })),
    ...(known === false && current.length > 0
      ? [{ value: current, label: relationLabel(current, customPairs.value) }]
      : []),
  ];
});

function accentForFile(file: string): Maybe<string> {
  if (file.length === 0) return nothing;
  if (
    matchMaybe(session.activeDatabase, {
      just: (active) => active === file,
      nothing: () => false,
    })
  ) {
    return entityEffectiveColor(
      entityFields.fields.color,
      entityFields.fields.type,
    );
  }
  const entry = toMaybe(
    outlineEntities.databaseEntryFields.find((item) => item.name === file),
  );
  if (isNothing(entry)) return nothing;
  return entityEffectiveColor(
    entry.value.fields.color,
    entry.value.fields.type,
  );
}

function nodeCardStyle(file: string): Maybe<Record<string, string>> {
  return entityChipAccentStyle(accentForFile(file));
}

function nodeNameStyle(file: string): Maybe<Record<string, string>> {
  return matchMaybe(entityGraphNodeLabelStyle(accentForFile(file)), {
    nothing: () => nothing,
    just: (style) =>
      style.label.length > 0 ? just({ color: style.label }) : nothing,
  });
}

const sourceCardStyle = computed(() => nodeCardStyle(props.sourceFile));
const sourceNameStyle = computed(() => nodeNameStyle(props.sourceFile));
const targetCardStyle = computed(() => nodeCardStyle(props.targetFile));
const targetNameStyle = computed(() => nodeNameStyle(props.targetFile));

function onRelationSelect(value: string): void {
  if (value === CUSTOM_RELATION_OPTION) {
    const current = props.relation;
    const isKnown = relationOptions.value.some(
      (option) => option.value === current,
    );
    const draft =
      current.length > 0 && isKnown === false
        ? current.startsWith("custom:")
          ? current.slice("custom:".length).replace(/_/g, " ")
          : current
        : "";
    void dispatchCustomRelation({ type: "Open", draft });
    return;
  }
  emit("update:relation", value);
}

function commitCustomRelation(): void {
  void dispatchCustomRelation({ type: "Commit" });
}

function cancelCustomRelation(): void {
  void dispatchCustomRelation({ type: "Cancel" });
}

const canSave = computed(
  () =>
    props.sourceFile.trim().length > 0 &&
    props.targetFile.trim().length > 0 &&
    props.relation.trim().length > 0 &&
    props.busy === false,
);

const displayRelation = computed(() =>
  relationLabel(props.relation, customPairs.value),
);
</script>

<template>
  <ZwDialog
    :open="open"
    :title="title"
    size="md"
    :description="nothing"
    @close="emit('close')"
  >
    <div class="entity-rel-graph">
      <div
        class="entity-rel-node entity-rel-node-source"
        :style="fromMaybe(sourceCardStyle)"
      >
        <span
          class="entity-rel-node-name"
          :style="fromMaybe(sourceNameStyle)"
          >{{ displayFileName(sourceFile) }}</span
        >
      </div>

      <div class="entity-rel-edge">
        <span class="entity-rel-shaft" aria-hidden="true" />
        <ZwSearchableMenu
          class="entity-rel-relation-menu"
          :options="relationOptions"
          search-placeholder="Search relationships…"
          :menu-label="just('Relationship')"
          @select="onRelationSelect"
        >
          <template #trigger="{ toggle, attrs }">
            <button
              type="button"
              class="entity-rel-relation"
              v-bind="attrs"
              title="Change relationship"
              @click="toggle()"
            >
              {{ displayRelation }}
            </button>
          </template>
          <template #footer>
            <ZwMenuItem
              label="Custom…"
              :shortcut="nothing"
              :title="nothing"
              @select="onRelationSelect(CUSTOM_RELATION_OPTION)"
            />
          </template>
        </ZwSearchableMenu>
        <span class="entity-rel-shaft-end" aria-hidden="true">
          <span class="entity-rel-shaft" />
          <svg
            class="entity-rel-arrowhead"
            viewBox="0 0 12 10"
            width="12"
            height="10"
          >
            <path d="M 0 0 L 12 0 L 6 10 z" />
          </svg>
        </span>
      </div>

      <div
        class="entity-rel-node entity-rel-node-target"
        :style="fromMaybe(targetCardStyle)"
      >
        <ZwSearchableMenu
          v-if="allowTargetChange"
          class="entity-rel-target-menu"
          :options="linkableEntries"
          :disabled="linkableEntries.length === 0"
          search-placeholder="Search entries…"
          :menu-label="just('Entry')"
          @select="emit('update:targetFile', $event)"
        >
          <template #trigger="{ toggle, attrs }">
            <button
              type="button"
              class="entity-rel-node-name entity-rel-node-pick"
              v-bind="attrs"
              :style="fromMaybe(targetNameStyle)"
              :disabled="linkableEntries.length === 0"
              @click="toggle()"
            >
              {{ targetLabel }}
            </button>
          </template>
        </ZwSearchableMenu>
        <span
          v-else
          class="entity-rel-node-name"
          :style="fromMaybe(targetNameStyle)"
          >{{ targetLabel }}</span
        >
      </div>
    </div>

    <ZwInput
      :id="nothing"
      :model-value="note"
      :label="just('Note')"
      placeholder="Optional"
      @update:model-value="emit('update:note', $event)"
    />

    <template #footer>
      <ZwButton
        v-if="editing"
        class="entity-rel-dialog-remove"
        :disabled="busy"
        @click="emit('remove')"
      >
        Remove
      </ZwButton>
      <ZwButton :disabled="busy" @click="emit('close')">Cancel</ZwButton>
      <ZwButton variant="primary" :disabled="!canSave" @click="emit('save')">
        {{ editing ? "Save relationship" : "Add relationship" }}
      </ZwButton>
    </template>
  </ZwDialog>

  <ZwDialog
    :open="customRelationDialogOpen"
    title="Custom relationship"
    :description="nothing"
    @close="cancelCustomRelation"
  >
    <ZwInput
      :id="nothing"
      v-model="customRelationDraft"
      :label="just('This side')"
      @keydown.enter.prevent="commitCustomRelation"
    />
    <ZwInput
      :id="nothing"
      v-model="customRelationInverseDraft"
      :label="just('Other side')"
      placeholder="Optional"
      @keydown.enter.prevent="commitCustomRelation"
    />
    <p class="entity-rel-custom-hint">
      Leave the other side blank for a symmetric pair (same label both ways).
    </p>
    <template #footer>
      <ZwButton @click="cancelCustomRelation">Cancel</ZwButton>
      <ZwButton
        variant="primary"
        :disabled="customRelationDraft.trim().length === 0"
        @click="commitCustomRelation"
        >Set relationship</ZwButton
      >
    </template>
  </ZwDialog>
</template>

<style scoped>
.entity-rel-graph {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  margin: 0.35rem 0 var(--space-lg);
  min-width: 0;
}

.entity-rel-node {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 3.75rem;
  padding: 1.15rem 1.25rem;
  border: 1px solid var(--line);
  border-radius: 0.4rem;
  background: var(--bg-raised);
  text-align: center;
}

.entity-rel-node-name {
  display: block;
  overflow: hidden;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--text-strong);
  overflow-wrap: anywhere;
}

.entity-rel-target-menu {
  width: 100%;
}

.entity-rel-node-pick {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  width: 100%;
  max-width: 100%;
  text-align: center;
  font: inherit;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.35;
  color: inherit;
  cursor: pointer;
  overflow-wrap: anywhere;
}

.entity-rel-node-pick:hover:not(:disabled),
.entity-rel-node-pick:focus-visible:not(:disabled) {
  color: var(--color-zw-amber);
}

.entity-rel-node-pick:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.entity-rel-edge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  align-self: center;
  min-height: 4rem;
  padding: 0.2rem 0;
}

.entity-rel-shaft {
  flex: 1 1 auto;
  width: 1.5px;
  min-height: 1rem;
  background: var(--color-zw-amber);
}

.entity-rel-shaft-end {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 1.25rem;
}

.entity-rel-shaft-end .entity-rel-shaft {
  flex: 1 1 auto;
  min-height: 0.75rem;
}

.entity-rel-arrowhead {
  display: block;
  flex: 0 0 auto;
  margin-top: -1px;
  fill: var(--color-zw-amber);
}

.entity-rel-relation-menu {
  flex: 0 0 auto;
}

.entity-rel-relation {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.2rem 0.35rem;
  font: inherit;
  font-size: 0.88rem;
  color: var(--text-muted);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 0.18em;
  white-space: nowrap;
}

.entity-rel-relation:hover,
.entity-rel-relation:focus-visible {
  color: var(--color-zw-amber);
}

.entity-rel-custom-hint {
  margin: 0.35rem 0 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: var(--text-muted);
}

.entity-rel-dialog-remove {
  margin-right: auto;
  color: var(--danger);
}
</style>
